import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '@/services/api';
import { Evaluation, Task, TaskResult } from '@/types';
import PageLayout from '@/shared/ui/layout/PageLayout';
import PublicLayout from '@/shared/ui/layout/PublicLayout'; // Import PublicLayout
import { DetailSection, GroupedContent } from '@/shared/ui/layout';
import { calculateCategoryScore } from '@/shared/utils/scoreCalculation';
import Tooltip from '@/shared/ui/feedback/Tooltip';
import './styles.css';

// Helper function to group results by category
const groupResultsByCategory = (results: TaskResult[]) => {
  const grouped: Record<string, TaskResult[]> = {};
  
  results.forEach(result => {
    const task = result.task as Task;
    const category = task.category || 'Uncategorized';
    
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(result);
  });
  
  // Sort categories alphabetically
  return Object.keys(grouped)
    .sort()
    .reduce((acc, category) => {
      acc[category] = grouped[category];
      return acc;
    }, {} as Record<string, TaskResult[]>);
};

// Helper function to get score status
const getScoreStatus = (result: TaskResult, task: Task) => {
  if (result.score === null) return 'not-evaluated';
  
  if (typeof result.score === 'boolean') {
    return result.score ? 'full-score' : 'zero-score';
  }
  
  if (task.scoringMethod === 'numerical' && task.scoringScale) {
    const { min, max } = task.scoringScale;
    const score = result.score as number;
    const percentage = (score - min) / (max - min);
    
    if (percentage >= 0.8) return 'full-score';
    if (percentage <= 0.2) return 'zero-score';
    return 'partial-score';
  }
  
  return 'not-evaluated';
};

const UserEvaluationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [groupedResults, setGroupedResults] = useState<Record<string, TaskResult[]>>({});
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const data = await api.user.getEvaluationById(id);
      setEvaluation(data);
      
      // Group results by category
      setGroupedResults(groupResultsByCategory(data.results));
      
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to fetch evaluation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (loading) return <div className="loading">Loading evaluation details...</div>;
  if (!evaluation) return <div>Evaluation not found</div>;

  const testId = typeof evaluation.test === 'string' ? evaluation.test : evaluation.test._id;

  const actions = (
    <div className="actions-group">
      <Link to={`/results/test/${testId}`} className="button">
        Back to Results
      </Link>
    </div>
  );

  // Calculate total score for display in details
  const calculateTotalScore = () => {
    if (!evaluation.results.length) return "0/0 (0%)";
    
    const scoreResult = calculateCategoryScore(evaluation.results);
    return scoreResult.displayText;
  };

  const testName = typeof evaluation.test === 'string' ? evaluation.test : evaluation.test.name;
  const pageTitle = `Evaluation of ${evaluation.modelName} on ${testName}`;

  const detailItems = [
    {
      label: 'Status:',
      value: <span className={`status ${evaluation.status}`}>{evaluation.status}</span>
    },
    {
      label: 'Test:',
      value: testName
    },
    {
      label: 'Tasks Evaluated:',
      value: evaluation.results.length
    },
    {
      label: 'Categories:',
      value: Object.keys(groupedResults).length
    },
    {
      label: 'Total Score:',
      value: calculateTotalScore()
    },
    {
      label: 'Completed:',
      value: new Date(evaluation.completedAt!).toLocaleString()
    }
  ];

  const renderResultGroup = (category: string, results: TaskResult[]) => (
    <div className="user-view-results-list">
      {results.map((result) => {
        const task = result.task as Task;
        const scoreStatus = getScoreStatus(result, task);
        
        // Format the score display
        let scoreDisplay;
        if (result.score === null) {
          scoreDisplay = 'Not evaluated';
        } else if (typeof result.score === 'boolean') {
          scoreDisplay = result.score ? 'Pass' : 'Fail';
        } else if (task.scoringMethod === 'numerical' && task.scoringScale) {
          scoreDisplay = `${result.score}/${task.scoringScale.max} (${Math.round((result.score / task.scoringScale.max) * 100)}%)`;
        } else {
          scoreDisplay = String(result.score);
        }
        
        return (
          <div 
            key={task._id} 
            className={`user-view-task-card ${scoreStatus}`}
            // onClick={() => navigate(`/tasks/${task._id}`)} // This line is removed
          >
            <h3 className="user-view-task-title">{task.title}</h3>
            
            <div 
              className={`user-view-body-wrapper ${task.attachments && task.attachments.length > 0 ? 'with-attachments' : ''}`}
            >
              <div className="user-view-content-box">
                <div className="user-view-text-content">
                  {task.content}
                </div>
              </div>

              {/* Display task attachments if any */}
              {task.attachments && task.attachments.length > 0 && (
                <div className="user-view-task-attachments">
                  <div className="user-view-attachments-list">
                    {task.attachments.map((attachment) => {
                      const isImage = attachment.mimetype.startsWith('image/');
                      const isPdf = attachment.mimetype === 'application/pdf';
                      const downloadUrl = api.tasks.getAttachmentUrl(task._id!, attachment._id!);
                      
                      return (
                        <div 
                          key={attachment._id} 
                          className="user-view-attachment-item"
                          onClick={(e) => e.stopPropagation()}
                          title={attachment.originalname}
                        >
                          {isImage ? (
                            <div className="user-view-image-preview">
                              <img src={downloadUrl} alt={attachment.originalname} />
                            </div>
                          ) : (
                            <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="user-view-file-link">
                              <div className="user-view-file-icon">
                                {isPdf ? '📄 PDF' : '📄'}
                              </div>
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="user-view-task-footer">
                <div className="user-view-task-meta">
                  <div className="user-view-score-display">
                    <div className="user-view-score-header">
                      <span className="user-view-score-label">Score:</span>
                      {task.scoringRules && (
                        <Tooltip text={task.scoringRules}>
                          <span className="user-view-rules-hint">
                            ⓘ
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    <span className={`user-view-score-value ${scoreStatus}`}>{scoreDisplay}</span>
                    {task.scoringMethod === 'numerical' && task.scoringScale && (
                      <span className="user-view-scale-info">
                        (Scale: {task.scoringScale.min} - {task.scoringScale.max})
                      </span>
                    )}
                  </div>
                  {result.notes && (
                    <div className="user-view-footer-notes">
                      <p className="user-view-notes-text">{result.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Custom title renderer for the GroupedContent component
  const renderCategoryTitle = (category: string) => {
    // Calculate category score using the utility function
    const scoreResult = calculateCategoryScore(groupedResults[category]);
    
    return (
      <div className="user-view-category-title-wrapper">
        <span>{category}</span>
        <span className="user-view-category-score">{scoreResult.displayText}</span>
      </div>
    );
  };

  return (
    <PublicLayout>
      <PageLayout
        title={pageTitle}
        actions={actions}
      >
        <div className="user-view-evaluation-content">
          <DetailSection items={detailItems} />

          <h3>Results by Category</h3>
          <div className="user-view-evaluation-results">
            {Object.keys(groupedResults).length === 0 ? (
              <div className="no-results">No results found.</div>
            ) : (
              <GroupedContent
                groups={groupedResults}
                renderGroup={renderResultGroup}
                groupTitle={renderCategoryTitle}
                emptyMessage="No results found."
              />
            )}
          </div>
        </div>
      </PageLayout>
    </PublicLayout>
  );
};

export default UserEvaluationDetail;
